


























































import { Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { mapState } from 'vuex';

import { required, email, minLength } from 'vuelidate/lib/validators';
import { formMixin } from '~/utils/form';

import EmailInput from '~/components/inputs/EmailInput.vue';
import PasswordInput from '~/components/inputs/PasswordInput.vue';
import ErrorCard from '~/components/cards/ErrorCard.vue';
import SocialSign from '~/components/lists/SocialSign.vue';
import passwordStrength from "~/utils/passwordStrength";

Component.registerHooks(['validations']);

@Component({
  layout: 'signin',
  components: {
    EmailInput,
    ErrorCard,
    PasswordInput,
    SocialSign,
  },
  computed: {
    ...mapState('user', {
      loginError: 'error',
    }),
  },
  middleware: 'noauth',
  head () {
    const self: any = this;
    return {
      title: `${self.$t('topMenu.signUpLabel')} - On Piste`,
      meta: [
        {
          hid: 'og:title',
          property: 'og:title',
          content: `${self.$t('topMenu.signUpLabel')} - On Piste`,
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: 'https://www.onpiste.com/img/sign/img__rossignol__mtb1.png',
        },
      ],
    };
  },
})
export default class SignUpEmail extends mixins(formMixin) {
  error: { response: { data: { detail: string } }} | string = "";
  email: string = "";
  password: string = "";
  loginError: any;

  /* Returns loginForm object from store */
  get loginForm (): Types.LoginForm {
    return this.$store.state.user.loginForm;
  };

  /* Returns boolean from loginForm that will need add some validation rules */
  get passwordRequired (): boolean {
    return !this.loginForm.provider;
  }

  /* Adds a disable status to submit button based on input values and store rules */
  get submitDisabled (): boolean {
    return !this.email || (this.passwordRequired && !this.password);
  }

  mounted () {
    this.$store.commit('user/SET_SIGN_IMAGE', '/img/sign/img__rossignol__mtb1.png');

    // Prefills email if exists in loginForm
    this.email = this.loginForm.email;

    // We can handle errors when arriving on page. This case can occures for
    // example when user is redirected to this page when next steps of signUp
    // process can't be accepted.
    // We will populate this.error based on state value this.loginError to have
    // a translated message in ErrorCard.
    if (this.loginError) {
      if (this.loginError.statusCode === 409) {
        this.error = {
          response: {
            data: {
              detail: this.$t('signup.email.error.409'),
            },
          },
        };
      } else {
        this.error = {
          response: {
            data: {
              detail: this.loginError.description,
            },
          },
        };
      }
    }
  }

  /*
    Returns validation object to be used in vuelidate. Here we want:
    - The email to be required, and be an email type
    - The password to be required and have a min length of 8 characters
  */
  validations () {
    const onlyIf = (cond: boolean, key: string, value: Object) => cond ? { [key]: value } : {};
    return {
      email: { required, email },
      ...onlyIf(this.passwordRequired, "password", { required, minLength: minLength(12), passwordStrength }),
    };
  }

  /*
    If using signUp with a social account for the first time, we will populate
    the loginFormso we can keep it in store for the next steps of the process
  */
  onNeedSignup (data: Types.SocialUserInfo) {
    this.$store.commit("user/SET_LOGIN_FORM", data);
    this.email = data.email || "";
    this.error = "";
  }

  /*
    If user has already signed up with social account, we will simply login the
    user and redirect to the profile.
  */
  onSignedIn () {
    const route: any = this.localePath("/profile/my-runs");
    this.$router.push(route);
  }

  /*
    If any error occures during social signUp process, we can display a
    message in ErrorCard
  */
  onError (error: string) {
    this.error = error;
  }

  /*
    When submitting the form, we don't send the API POST call right now as some
    more informations are needed for that on the next page. We store the form in
    the store so it will be added to the next form for the final submit.
  */
  submit () {
    this.formMixin_handleSubmit(() => {
      const partialInfo: Partial<Types.LoginForm> = {
        email: this.email,
        password: this.password,
      };
      this.$store.commit('user/SET_LOGIN_ERROR', null);
      this.$store.commit("user/SET_LOGIN_FORM", partialInfo);

      const route: any = this.localePath('/signup/profile');

      this.$router.push(route);
    });
  }
}
